import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { EmptyState, StatusBadge, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, ArrowRight, Boxes } from "lucide-react";

const num = (x) => Number(x) || 0;
const RAW_STATUS_L = { raw_pending: "raw pending", raw_completed: "raw completed" };

export default function RawProcessing() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [buyers, setBuyers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [products, setProducts] = useState([]);
  const [costings, setCostings] = useState([]);
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [rawOrders, setRawOrders] = useState([]);

  // buyer PO dialog
  const [poOpen, setPoOpen] = useState(false);
  const [po, setPo] = useState({ buyer_id: "", po_no: "", order_date: "", notes: "", lines: [{ sku: "", total_pcs: "" }] });
  // allocation dialog
  const [allocOpen, setAllocOpen] = useState(false);
  const [alloc, setAlloc] = useState({ buyer_id: "", buyer_order_id: "", sku: "", contractor: "", pcs: "", rate: "", notes: "" });
  const [openLines, setOpenLines] = useState([]);

  const skuOptions = useMemo(() => {
    const m = new Map();
    products.forEach((p) => p.sku && m.set(p.sku, p.name));
    costings.forEach((c) => c.sku && !m.has(c.sku) && m.set(c.sku, c.product_name));
    return [...m.entries()].map(([sku, name]) => ({ sku, name }));
  }, [products, costings]);

  const loadOrders = () => {
    api.get("/buyer-orders").then((r) => setBuyerOrders(r.data || []));
    api.get("/raw-orders").then((r) => setRawOrders(r.data || []));
  };
  useEffect(() => {
    api.get("/buyers").then((r) => setBuyers(r.data || []));
    api.get("/ledgers?type=contractor").then((r) => setContractors(r.data || []));
    api.get("/products").then((r) => setProducts(r.data || []));
    api.get("/costings").then((r) => setCostings(r.data || []));
    loadOrders();
  }, []);

  // ---- Buyer PO
  const poUp = (i, k, v) => setPo((p) => { const l = [...p.lines]; l[i] = { ...l[i], [k]: v }; return { ...p, lines: l }; });
  const savePo = async () => {
    try {
      const b = buyers.find((x) => x.id === po.buyer_id);
      const lines = po.lines.filter((l) => l.sku && num(l.total_pcs) > 0)
        .map((l) => ({ sku: l.sku, product_name: skuOptions.find((o) => o.sku === l.sku)?.name || "", total_pcs: num(l.total_pcs) }));
      if (!lines.length) { toast.error("Add at least one SKU with pieces"); return; }
      await api.post("/buyer-orders", { ...po, buyer_name: b?.name || "", lines });
      toast.success("Buyer PO saved");
      setPoOpen(false); setPo({ buyer_id: "", po_no: "", order_date: "", notes: "", lines: [{ sku: "", total_pcs: "" }] });
      loadOrders();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deletePo = async (id) => { await api.delete(`/buyer-orders/${id}`); toast.success("Deleted"); loadOrders(); };

  // ---- Allocation
  const openAlloc = () => { setAlloc({ buyer_id: "", buyer_order_id: "", sku: "", contractor: "", pcs: "", rate: "", notes: "" }); setOpenLines([]); setAllocOpen(true); };
  const pickBuyer = async (buyer_id) => {
    setAlloc((a) => ({ ...a, buyer_id, buyer_order_id: "", sku: "" }));
    try { const { data } = await api.get(`/buyer-orders/open?buyer_id=${buyer_id}`); setOpenLines((data || []).filter((l) => l.open_pcs > 0)); }
    catch { setOpenLines([]); }
  };
  const pickLine = async (key) => {
    const [boid, sku] = key.split("|");
    const ln = openLines.find((l) => l.buyer_order_id === boid && l.sku === sku);
    setAlloc((a) => ({ ...a, buyer_order_id: boid, sku, pcs: ln ? String(ln.open_pcs) : "" }));
    try {
      const { data } = await api.get(`/costings/by-sku/${encodeURIComponent(sku)}`);
      if (data?.raw_total != null) setAlloc((a) => ({ ...a, rate: String(data.raw_total) }));
    } catch (e) { console.warn("No cost sheet for SKU, rate left manual", e); }
  };
  const selectedLine = openLines.find((l) => l.buyer_order_id === alloc.buyer_order_id && l.sku === alloc.sku);
  const overAlloc = selectedLine && num(alloc.pcs) > selectedLine.open_pcs;
  const saveAlloc = async () => {
    if (overAlloc) { toast.error(`Only ${selectedLine.open_pcs} pcs open on this PO`); return; }
    try {
      const b = buyers.find((x) => x.id === alloc.buyer_id);
      const bo = buyerOrders.find((x) => x.id === alloc.buyer_order_id);
      const { data } = await api.post("/raw-orders", {
        buyer_id: alloc.buyer_id, buyer_name: b?.name || "", buyer_order_id: alloc.buyer_order_id,
        buyer_po_no: bo?.po_no || "", sku: alloc.sku, product_name: skuOptions.find((o) => o.sku === alloc.sku)?.name || "",
        contractor: alloc.contractor, pcs: num(alloc.pcs), rate: num(alloc.rate), notes: alloc.notes,
      });
      toast.success(data.generated_sos?.length ? `${data.rp_number} created · ${data.generated_sos.length} store order(s) issued` : `${data.rp_number} created`);
      setAllocOpen(false); loadOrders();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const completeRaw = async (ro) => {
    const to = ro.status === "raw_completed" ? "raw_pending" : "raw_completed";
    const { data } = await api.patch(`/raw-orders/${ro.id}/status`, { status: to });
    setRawOrders((prev) => prev.map((x) => (x.id === ro.id ? data : x)));
    if (to === "raw_completed") toast.success(data.finishing_sos?.length ? `Raw complete — ${data.finishing_sos.length} finishing order(s) auto-generated` : "Marked raw complete");
    else toast.info("Reopened");
  };
  const deleteRaw = async (id) => { await api.delete(`/raw-orders/${id}`); toast.success("Deleted"); loadOrders(); };

  return (
    <div className="space-y-6">
      {/* Buyer POs */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold"><Boxes className="h-4 w-4 text-primary" /> Buyer POs</h3>
          {canManage && (
            <Dialog open={poOpen} onOpenChange={setPoOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1" data-testid="add-buyer-po"><Plus className="h-3.5 w-3.5" /> New Buyer PO</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">New Buyer PO</DialogTitle></DialogHeader>
                <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Buyer</Label>
                      <Select value={po.buyer_id} onValueChange={(v) => setPo({ ...po, buyer_id: v })}>
                        <SelectTrigger data-testid="po-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                        <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1"><Label className="text-xs">Buyer PO No</Label>
                      <Input data-testid="po-no" value={po.po_no} onChange={(e) => setPo({ ...po, po_no: e.target.value })} placeholder="CB-2026-5567" /></div>
                    <div className="space-y-1"><Label className="text-xs">Order date</Label>
                      <Input type="date" value={po.order_date} onChange={(e) => setPo({ ...po, order_date: e.target.value })} /></div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <Label className="text-xs">SKUs & pieces</Label>
                      <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs text-primary" onClick={() => setPo((p) => ({ ...p, lines: [...p.lines, { sku: "", total_pcs: "" }] }))}><Plus className="h-3 w-3" /> Add SKU</Button>
                    </div>
                    {po.lines.map((l, i) => (
                      <div key={i} className="mb-1 grid grid-cols-12 gap-2">
                        <div className="col-span-7">
                          <Select value={l.sku} onValueChange={(v) => poUp(i, "sku", v)}>
                            <SelectTrigger data-testid={`po-sku-${i}`}><SelectValue placeholder="SKU" /></SelectTrigger>
                            <SelectContent>{skuOptions.map((o) => <SelectItem key={o.sku} value={o.sku}>{o.sku} · {o.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Input className="col-span-4" type="text" inputMode="decimal" placeholder="Total pcs" data-testid={`po-pcs-${i}`} value={l.total_pcs} onChange={(e) => poUp(i, "total_pcs", e.target.value)} />
                        <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9" onClick={() => setPo((p) => ({ ...p, lines: p.lines.filter((_, x) => x !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter><Button data-testid="po-save" onClick={savePo} disabled={!po.buyer_id || !po.po_no}>Save Buyer PO</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {buyerOrders.length === 0 ? (
          <p className="text-sm text-muted-foreground">No buyer POs yet. Add one to allocate its pieces for raw processing.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {buyerOrders.map((o) => (
              <Card key={o.id} data-testid={`buyer-po-${o.id}`} className="rounded-md border-border p-4 shadow-none">
                <div className="flex items-start justify-between">
                  <div><p className="font-semibold">{o.po_no}</p><p className="text-xs text-muted-foreground">{o.buyer_name} · {o.order_date || "—"}</p></div>
                  {canManage && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deletePo(o.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
                <div className="mt-2 space-y-1">
                  {(o.lines || []).map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-xs"><span className="truncate">{l.sku} · {l.product_name}</span><span className="font-semibold">{l.total_pcs} pcs</span></div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Allocations */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold"><ClipboardList className="h-4 w-4 text-primary" /> Raw Processing Allocations</h3>
          {canManage && <Button size="sm" className="gap-1" data-testid="add-alloc" onClick={openAlloc}><Plus className="h-3.5 w-3.5" /> Allocate to Contractor</Button>}
        </div>
        {rawOrders.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No allocations yet" hint="Allocate open buyer-PO pieces to a contractor. Wood & Hardware store orders auto-generate." />
        ) : (
          <Card className="overflow-hidden rounded-md border-border shadow-none">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>RP #</TableHead><TableHead>Buyer PO</TableHead><TableHead>SKU</TableHead><TableHead>Contractor</TableHead>
                  <TableHead className="text-right">Pcs</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Total</TableHead>
                  <TableHead>Store orders</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rawOrders.map((ro) => (
                  <TableRow key={ro.id} data-testid={`raw-row-${ro.id}`} className="hover:bg-muted/40">
                    <TableCell className="font-semibold">{ro.rp_number}</TableCell>
                    <TableCell>{ro.buyer_po_no}<div className="text-xs text-muted-foreground">{ro.buyer_name}</div></TableCell>
                    <TableCell>{ro.sku}</TableCell>
                    <TableCell>{ro.contractor || "—"}</TableCell>
                    <TableCell className="text-right">{ro.pcs}</TableCell>
                    <TableCell className="text-right">{money(ro.rate)}</TableCell>
                    <TableCell className="text-right font-heading font-bold">{money(ro.total)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[...(ro.generated_sos || []), ...(ro.finishing_sos || [])].join(", ") || "—"}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-full text-[11px] font-semibold ${ro.status === "raw_completed" ? "border-primary/30 bg-primary/10 text-primary" : "border-ochre/30 bg-ochre/10 text-ochre"}`}>{RAW_STATUS_L[ro.status] || ro.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid={`raw-complete-${ro.id}`} onClick={() => completeRaw(ro)}>
                          <ArrowRight className="h-3.5 w-3.5" /> {ro.status === "raw_completed" ? "Reopen" : "Complete"}
                        </Button>
                        {canManage && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`raw-delete-${ro.id}`} onClick={() => deleteRaw(ro.id)}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Allocation dialog */}
      <Dialog open={allocOpen} onOpenChange={setAllocOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Allocate to Contractor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Buyer</Label>
              <Select value={alloc.buyer_id} onValueChange={pickBuyer}>
                <SelectTrigger data-testid="alloc-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">SKU (open on buyer POs)</Label>
              <Select value={alloc.buyer_order_id ? `${alloc.buyer_order_id}|${alloc.sku}` : ""} onValueChange={pickLine}>
                <SelectTrigger data-testid="alloc-sku"><SelectValue placeholder={alloc.buyer_id ? "Select SKU" : "Pick a buyer first"} /></SelectTrigger>
                <SelectContent>
                  {openLines.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No open pieces</div>}
                  {openLines.map((l) => <SelectItem key={`${l.buyer_order_id}|${l.sku}`} value={`${l.buyer_order_id}|${l.sku}`}>{l.sku} · {l.po_no} · {l.open_pcs} open</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedLine && <p className="text-[11px] text-muted-foreground">{selectedLine.open_pcs} of {selectedLine.total_pcs} pcs open on {selectedLine.po_no}</p>}
            </div>
            <div className="space-y-1"><Label className="text-xs">Contractor</Label>
              <Select value={alloc.contractor} onValueChange={(v) => setAlloc((a) => ({ ...a, contractor: v }))}>
                <SelectTrigger data-testid="alloc-contractor"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="mt-1" value={alloc.contractor} onChange={(e) => setAlloc((a) => ({ ...a, contractor: e.target.value }))} placeholder="or type a name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Pieces to allocate</Label>
                <Input type="text" inputMode="decimal" data-testid="alloc-pcs" value={alloc.pcs} onChange={(e) => setAlloc((a) => ({ ...a, pcs: e.target.value }))} className={overAlloc ? "border-destructive" : ""} /></div>
              <div className="space-y-1"><Label className="text-xs">Rate / pc ₹ (prefilled)</Label>
                <Input type="text" inputMode="decimal" data-testid="alloc-rate" value={alloc.rate} onChange={(e) => setAlloc((a) => ({ ...a, rate: e.target.value }))} /></div>
            </div>
            <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-xs">
              <span className="text-muted-foreground">Allocation total</span>
              <span className="font-heading font-bold text-primary">{money(num(alloc.pcs) * num(alloc.rate))}</span>
            </div>
            <div className="space-y-1"><Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={alloc.notes} onChange={(e) => setAlloc((a) => ({ ...a, notes: e.target.value }))} placeholder="Instructions for the contractor…" /></div>
            <p className="text-[11px] text-muted-foreground">On save, Wood &amp; Hardware store issue orders auto-generate from this SKU's BOM × pieces. Polish, packing &amp; final-hardware orders generate when you mark this raw processing complete.</p>
          </div>
          <DialogFooter><Button data-testid="alloc-save" onClick={saveAlloc} disabled={!alloc.buyer_order_id || !alloc.contractor || !num(alloc.pcs) || overAlloc}>Allocate &amp; Issue Orders</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
